import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, CreditCard, Layers, ShieldCheck, AlertCircle, ScrollText, ArrowUpRight, Activity, Database } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AdminAPI } from '@/lib/admin-api';
import { formatDistanceToNow } from '@/lib/format-time';

const StatTile = ({ label, value, sub, icon: Icon, accent }) => (
  <Card className="p-4" data-testid={`admin-stat-${(label || '').toLowerCase()}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      {Icon && (
        <div className={`h-7 w-7 rounded-md grid place-items-center ${accent || 'bg-[hsl(var(--surface-2))] text-foreground/80'}`}>
          <Icon className="h-[14px] w-[14px]" />
        </div>
      )}
    </div>
    <div className="mt-2 font-display text-2xl font-semibold tracking-tight font-mono-num">{value}</div>
    {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
  </Card>
);

const ACTION_BADGE = {
  create: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  update: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  delete: 'bg-rose-500/10 text-rose-600 border-rose-500/30',
  publish: 'bg-primary/10 text-primary border-primary/30',
  archive: 'bg-slate-500/10 text-slate-600 border-slate-500/30',
  rollback: 'bg-accent/10 text-accent border-accent/30',
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    AdminAPI.stats()
      .then(setStats)
      .catch((e) => setError(e.message || 'Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-medium">Operations</div>
          <h1 className="mt-1.5 font-display text-xl sm:text-2xl font-semibold tracking-tight">Reward Ops Dashboard</h1>
          <p className="mt-1 text-xs text-muted-foreground max-w-2xl">
            The operational source-of-truth for banks, cards, reward DSL rules, milestones and the underlying intelligence layers. Edits here flow into the consumer app.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild size="sm" variant="outline" data-testid="admin-dashboard-go-banks">
            <Link to="/admin/banks">Manage banks</Link>
          </Button>
          <Button asChild size="sm" data-testid="admin-dashboard-go-cards">
            <Link to="/admin/cards">Manage cards</Link>
          </Button>
        </div>
      </div>

      {error && (
        <Card className="p-3 border-rose-500/30 bg-rose-500/5 flex items-start gap-2 text-sm text-rose-700 dark:text-rose-300" data-testid="admin-error-alert">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <span>{error}</span>
        </Card>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-[88px] w-full rounded-xl" />
          ))}
        </div>
      ) : stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile label="Banks" value={stats.banks.total} sub={`${stats.banks.active} active`} icon={Building2} />
          <StatTile label="Cards" value={stats.cards.total} sub={`${stats.cards.active} live · ${stats.cards.draft} draft`} icon={CreditCard} />
          <StatTile label="Rule versions" value={stats.rule_versions.total} sub={`${stats.rule_versions.active} active · ${stats.rule_versions.draft} drafts`} icon={Layers} accent="bg-primary/10 text-primary" />
          <StatTile label="Audit events" value={stats.audits.total} sub="All-time" icon={ScrollText} />
          <StatTile label="MCCs" value={stats.mccs.total} icon={Database} />
          <StatTile label="Offers" value={stats.offers.total} icon={Activity} />
          <StatTile label="Merchants" value={stats.merchants.total} icon={Activity} />
          <StatTile label="Whitelist" value={'Active'} sub="Email-based admin gate" icon={ShieldCheck} accent="bg-emerald-500/10 text-emerald-600" />
        </div>
      )}

      {stats && (
        <div className="grid lg:grid-cols-3 gap-3">
          <Card className="p-4 lg:col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">Recent activity</div>
                <p className="text-[11px] text-muted-foreground">Last 5 admin actions across all entities</p>
              </div>
              <Button asChild size="sm" variant="ghost" className="gap-1.5 text-muted-foreground hover:text-foreground" data-testid="admin-dashboard-view-audit">
                <Link to="/admin/audit-log">View all <ArrowUpRight className="h-3.5 w-3.5" /></Link>
              </Button>
            </div>
            <div className="mt-3 space-y-2" data-testid="admin-dashboard-recent-audits">
              {(stats.audits.recent || []).length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-foreground">
                  No activity yet. Make your first edit to see it tracked here.
                </div>
              ) : (
                stats.audits.recent.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 py-1.5 border-b border-border/40 last:border-0">
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wide px-1.5 ${ACTION_BADGE[e.action] || ''}`}>{e.action}</Badge>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate">
                        <span className="text-muted-foreground">{e.entity_type}</span>{' '}
                        <span className="font-mono-num text-xs">{e.entity_id}</span>{' '}
                        <span className="text-muted-foreground">—</span> {e.diff_summary}
                      </div>
                      <div className="text-[10px] text-muted-foreground">{e.actor_email || '—'} · {formatDistanceToNow(e.timestamp)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-sm font-semibold">Workflow tips</div>
            <ul className="mt-2 space-y-2 text-[12px] text-muted-foreground">
              <li className="flex gap-2"><span className="text-accent">•</span> All reward rules are versioned. Edits create drafts; publishing archives the previous version automatically.</li>
              <li className="flex gap-2"><span className="text-accent">•</span> Use the simulator (under Cards → Rules) to verify a draft before publishing.</li>
              <li className="flex gap-2"><span className="text-accent">•</span> Deletes are soft — referenced entities are marked inactive instead of removed.</li>
              <li className="flex gap-2"><span className="text-accent">•</span> The consumer app reads the active version through the canonical cards_library mirror.</li>
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
}
